import Skeleton from "@/v2-ui/skeleton"
import cn from "@/v2-ui/utils/utils.cn"

export type LicenseFeatureGroupComposeSkeletonProps = {
  /** Card columns, as the view it stands in for lays them out; 1 is a list of short cards (the package picker). */
  columns?: 1 | 2 | 3
  /** Whether to draw the view switch above the cards (the whole editor) or only the cards (one view). */
  withViewSwitch?: boolean
  className?: string
}

// Placeholder rows, each with an id for React and a width so the stand-in
// looks like text of varying length rather than a ruler.
const PLACEHOLDER_ROWS = [
  { id: "a", width: "w-32" },
  { id: "b", width: "w-24" },
  { id: "c", width: "w-40" },
  { id: "d", width: "w-28" },
  { id: "e", width: "w-36" },
  { id: "f", width: "w-20" },
  { id: "g", width: "w-32" },
  { id: "h", width: "w-28" },
  { id: "i", width: "w-24" },
] as const

const ONE_COLUMN_CARDS = [
  { id: "first", rows: 1 },
  { id: "second", rows: 1 },
  { id: "third", rows: 1 },
  { id: "fourth", rows: 1 },
  { id: "fifth", rows: 1 },
  { id: "sixth", rows: 1 },
]

const THREE_COLUMN_CARDS = [
  { id: "map", rows: 5 },
  { id: "property", rows: 8 },
  { id: "filter", rows: 3 },
  { id: "company", rows: 6 },
  { id: "building", rows: 4 },
  { id: "permit", rows: 7 },
] as const
const TWO_COLUMN_CARDS = [
  { id: "basemaps", rows: 6 },
  { id: "layers", rows: 9 },
] as const

function CardSkeleton(props: { rows: number }) {
  const { rows } = props
  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-2.5 border-b bg-muted/40 px-2.5 py-2">
        <Skeleton className="size-3.5 rounded-sm" />
        <Skeleton className="h-3 w-28" />
        <Skeleton className="ml-auto h-3 w-8" />
      </div>
      <ul className="divide-y">
        {PLACEHOLDER_ROWS.slice(0, rows).map((row) => (
          <li key={row.id} className="flex items-center gap-2.5 px-2 py-1.5">
            <Skeleton className="size-3.5 rounded-sm" />
            <Skeleton className={cn("h-3", row.width)} />
          </li>
        ))}
      </ul>
    </div>
  )
}

// The editor's shape while it loads, so the page does not jump from a
// spinner in the void to a wall of cards: the view switch, the section
// line, and a grid of cards with a header and a few rows each — the same
// geometry the real thing lands in.
function LicenseFeatureGroupComposeSkeleton(
  props: LicenseFeatureGroupComposeSkeletonProps,
) {
  const { columns = 3, withViewSwitch = false, className } = props
  const cards =
    columns === 3
      ? THREE_COLUMN_CARDS
      : columns === 2
        ? TWO_COLUMN_CARDS
        : ONE_COLUMN_CARDS
  return (
    <div
      className={cn("flex flex-col gap-3", className)}
      aria-busy="true"
      aria-label="Loading the package"
    >
      {withViewSwitch && (
        <div className="flex gap-1">
          <Skeleton className="h-7 w-28 rounded-sm" />
          <Skeleton className="h-7 w-20 rounded-sm" />
          <Skeleton className="h-7 w-36 rounded-sm" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-56" />
      </div>
      <div
        className={cn(
          "grid grid-cols-1 items-start gap-3",
          columns === 3 && "md:grid-cols-2 xl:grid-cols-3",
          columns === 2 && "md:grid-cols-2",
        )}
      >
        {cards.map((card) => (
          <CardSkeleton key={card.id} rows={card.rows} />
        ))}
      </div>
    </div>
  )
}

export default LicenseFeatureGroupComposeSkeleton
